/**
 * Bento Showcase — moderne „Bento-Box"-Feature-Grid.
 *
 * Ein 2026-typisches Bento-Raster aus Glas-Kacheln (erste Kachel als breites
 * Hero-Feld), jede mit Icon-Disc, Titel und Caption. Allround-Szene: Feature-
 * Übersicht, „Was du bekommst", Platzfüller mit Substanz oder Mini-Mockup.
 *
 * Motion:
 *   - Optionaler Header (Eyebrow + Headline) steigt ein.
 *   - Kachel für Kachel poppt mit Spring nach oben rein (Stagger), das Icon
 *     snappt nach, Titel/Caption blenden auf.
 *   - Ein weicher diagonaler Lichtstreifen wandert einmal über das ganze Raster.
 *
 * Items-Format:  "Titel | Caption | iconKey"
 *   z. B. "0 % Provision | Restaurants & Produkte | euro"
 * (iconKey optional; valide Keys siehe lucideIcon in _mockHelpers.)
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "./_mockHelpers.js";

export const bentoShowcase: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary;
  const pink = themeColors.accent;
  const palette = [accent, violet, pink, accent, violet];
  const isVertical = ctx.format === "9:16";

  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "";
  const headline = inputs.text ?? "";

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "Alles an einem Ort | 7 Kategorien, eine App | store",
        "0 % Risiko | Provision oder Flatrate | euro",
        "Echte Anbieter | Verifiziert & lokal | shield",
        "Sicher bezahlen | Stripe & Käuferschutz | lock",
        "Direkter Kontakt | Ohne Umwege | chat",
      ]
  ).slice(0, 5);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const eyeId = `${clipPrefix}-eye`;
  const gridId = `${clipPrefix}-grid`;
  const sweepId = `${clipPrefix}-sweep`;

  const tileIds = items.map((_, i) => `${clipPrefix}-tile-${i}`);
  const iconIds = items.map((_, i) => `${clipPrefix}-ic-${i}`);
  const titleIds = items.map((_, i) => `${clipPrefix}-tt-${i}`);
  const capIds = items.map((_, i) => `${clipPrefix}-cap-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const parsed = items.map((raw, i) => {
    const [title, caption, iconRaw] = splitItem(raw);
    return {
      title: (title ?? "").trim(),
      caption: (caption ?? "").trim(),
      icon: (iconRaw ?? "").trim() || "star",
      color: palette[i % palette.length] ?? accent,
    };
  });

  const titlePx = isVertical ? md * 0.78 : md * 0.74;
  const heroTitlePx = isVertical ? lg * 0.72 : lg * 0.7;
  const capPx = isVertical ? sm * 0.56 : sm * 0.58;
  const iconBox = isVertical ? 70 : 78;

  // Bento-Platzierung: erste Kachel = breites Hero-Feld (span 2 Spalten),
  // danach 2×2. Funktioniert auch mit weniger Items (leere Slots entfallen).
  function tileStyle(i: number, color: string, hero: boolean): string {
    const span = hero ? "grid-column:span 2;" : "";
    return `${span}position:relative;display:flex;flex-direction:column;justify-content:flex-end;gap:${hero ? 14 : 10}px;padding:${hero ? (isVertical ? 30 : 36) : isVertical ? 22 : 26}px;border-radius:26px;background:linear-gradient(160deg, ${withAlpha(themeColors.fg, 0.07)} 0%, ${withAlpha(themeColors.bg, 0.7)} 100%);border:1px solid ${withAlpha(color, 0.4)};overflow:hidden;will-change:transform,opacity;box-shadow:0 22px 48px rgba(0,0,0,0.4);min-height:${hero ? (isVertical ? 190 : 210) : isVertical ? 150 : 170}px;`;
  }

  function renderTile(i: number, it: (typeof parsed)[number]): string {
    const hero = i === 0;
    const color = it.color;
    return `<div id="${tileIds[i]}" style="${tileStyle(i, color, hero)}">
      <div style="${`position:absolute;inset:0;background:radial-gradient(120% 90% at 18% 0%, ${withAlpha(color, 0.32)} 0%, ${withAlpha(color, 0.05)} 45%, transparent 72%);`}"></div>
      <div id="${iconIds[i]}" style="position:relative;z-index:2;width:${iconBox}px;height:${iconBox}px;border-radius:20px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 30% 30%, ${withAlpha(color, 0.5)} 0%, ${withAlpha(color, 0.14)} 65%, ${withAlpha(themeColors.bg, 0.85)} 100%);border:1px solid ${withAlpha(color, 0.55)};box-shadow:0 0 26px ${withAlpha(color, 0.45)};color:${themeColors.fg};margin-bottom:auto;">
        ${lucideIcon(it.icon, Math.round(iconBox * 0.5), themeColors.fg, 2.1)}
      </div>
      <div id="${titleIds[i]}" style="position:relative;z-index:2;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${hero ? heroTitlePx : titlePx}px;line-height:1.02;letter-spacing:-1px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(it.title)}</div>
      ${it.caption ? `<div id="${capIds[i]}" style="position:relative;z-index:2;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${capPx}px;letter-spacing:0.2px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.74)};">${escapeHtml(it.caption)}</div>` : `<div id="${capIds[i]}" style="opacity:0;"></div>`}
    </div>`;
  }

  const tilesHtml = parsed.map((it, i) => renderTile(i, it)).join("");
  const eyePx = sm * 0.62;
  const headPx = isVertical ? lg * 0.92 : lg * 0.84;
  const hasHeader = Boolean(headline || eyebrow);

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 30 : 38}px;padding:${isVertical ? "84px 56px" : "96px 130px"};overflow:hidden;">
    ${hasHeader ? `<div style="display:flex;flex-direction:column;align-items:center;gap:12px;text-align:center;max-width:${isVertical ? 900 : 1200}px;">
      ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};display:inline-flex;align-items:center;gap:14px;"><span style="width:34px;height:2px;background:${accent};"></span><span>${escapeHtml(eyebrow)}</span></div>` : ""}
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.04;letter-spacing:-1.8px;color:${themeColors.fg};text-wrap:balance;text-shadow:0 0 50px ${withAlpha(accent, 0.35)};">${escapeHtml(headline)}</div>` : ""}
    </div>` : ""}
    <div id="${gridId}" style="position:relative;display:grid;grid-template-columns:1fr 1fr;gap:${isVertical ? 16 : 20}px;width:100%;max-width:${isVertical ? 940 : 1320}px;">
      ${tilesHtml}
    </div>
    <div id="${sweepId}" style="position:absolute;top:-8%;bottom:-8%;left:0;width:42%;background:linear-gradient(100deg, transparent 0%, ${withAlpha(themeColors.fg, 0.16)} 50%, transparent 100%);pointer-events:none;mix-blend-mode:screen;transform:rotate(8deg);z-index:5;"></div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: -8 }) : "",
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 22 }) : "",
    gsapSetLine(`#${sweepId}`, { opacity: 0, xPercent: -140 }),
    ...tileIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 46, scale: 0.92, transformOrigin: "50% 100%" })),
    ...iconIds.map((id) => gsapSetLine(`#${id}`, { scale: 0, transformOrigin: "50% 50%" })),
    ...titleIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 12 })),
    ...capIds.map((id, i) => (parsed[i].caption ? gsapSetLine(`#${id}`, { opacity: 0, y: 8 }) : "")),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.1));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.2));

  const tileStart = hasHeader ? 0.55 : 0.25;
  const entryWindow = Math.min(1.8, Math.max(0.7, clipDurationSec * 0.5));
  const step = entryWindow / Math.max(parsed.length, 1);

  parsed.forEach((it, i) => {
    const at = tileStart + i * step;
    tweens.push(t.to(`#${tileIds[i]}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, at));
    tweens.push(t.to(`#${iconIds[i]}`, { scale: 1, duration: 0.5, ease: "raw:\"back.out(2.2)\"" }, at + 0.1));
    tweens.push(t.to(`#${titleIds[i]}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, at + 0.14));
    if (it.caption) tweens.push(t.to(`#${capIds[i]}`, { opacity: 1, y: 0, duration: 0.38, ease: "raw:\"power2.out\"" }, at + 0.2));
  });

  const sweepAt = tileStart + parsed.length * step + 0.05;
  const sweepEnd = clipDurationSec - 10 / 30 - 0.2;
  if (sweepEnd - sweepAt > 0.5) {
    tweens.push(t.to(`#${sweepId}`, { opacity: 1, duration: 0.2, ease: "raw:\"none\"" }, sweepAt));
    tweens.push(t.to(`#${sweepId}`, { xPercent: 360, duration: Math.min(1.2, sweepEnd - sweepAt - 0.2), ease: "raw:\"power1.inOut\"" }, sweepAt + 0.05));
    tweens.push(t.to(`#${sweepId}`, { opacity: 0, duration: 0.25, ease: "raw:\"none\"" }, sweepEnd - 0.1));
  }

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
